import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { 
  Plus, Search, Edit, Trash2, Save, X, 
  FileText, Upload, Download, Filter, Check, AlertCircle,
  BookOpen, Layers, FolderOpen, ArrowLeft, RefreshCw,
  CheckCircle, Star, Calendar, Zap, Eye, EyeOff, BarChart3
} from 'lucide-react';
import { toast } from 'sonner';
import { renderLatex } from '../lib/renderLatex';
import { compressImage } from '../lib/compressImage';

// Helper function to parse matching list questions
const parseMatchingList = (text) => {
  if (!text) return null;
  
  const lowerText = text.toLowerCase();
  if (!lowerText.includes('match') && !lowerText.includes('list')) return null;
  
  // Clean up weird characters
  const cleanText = text.replace(/▌+/g, '').replace(/\s+/g, ' ');
  
  // Extract List-I items (A, B, C, D, etc.)
  const listIItems = [];
  const listIRegex = /([A-D])\.\s*([^A-D]+?)(?=\s*[A-D]\.|I\.|\||$)/gi;
  let match;
  
  while ((match = listIRegex.exec(cleanText)) !== null) {
    const label = match[1].trim();
    let text = match[2].trim();
    
    // Stop if we hit Roman numerals
    if (text.match(/^\s*I{1,3}\./)) break;
    
    // Remove trailing "I.", "II.", etc.
    text = text.replace(/\s+(I{1,4}|IV)\.\s*\[.+$/, '').trim();
    
    if (text && text.length > 2) {
      listIItems.push({ label, text });
    }
  }
  
  // Extract List-II items (I, II, III, IV, etc.)
  const listIIItems = [];
  const listIIRegex = /(I{1,4}|IV)\.\s*(\[.+?\])/gi;
  
  while ((match = listIIRegex.exec(cleanText)) !== null) {
    listIIItems.push({
      label: match[1].trim(),
      text: match[2].trim()
    });
  }
  
  // Need at least 2 items in each list to be valid
  if (listIItems.length >= 2 && listIIItems.length >= 2) {
    // Extract the question header (text before first "A.")
    const headerMatch = cleanText.match(/^(.+?)(?=\s*A\.)/i);
    const header = headerMatch ? headerMatch[1].trim() : 'Match the following:';
    
    return { 
      header, 
      listI: listIItems.slice(0, 4), // Max 4 items
      listII: listIIItems.slice(0, 4) 
    };
  }
  
  return null;
};

// Render matching list as a table
const renderMatchingList = (parsedData) => {
  if (!parsedData) return null;
  
  const maxRows = Math.max(parsedData.listI.length, parsedData.listII.length);
  
  return (
    <div className="my-3">
      {parsedData.header && (
        <div className="text-sm mb-3 font-medium" dangerouslySetInnerHTML={{ __html: renderLatex(parsedData.header) }} />
      )}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse border border-border rounded-lg overflow-hidden">
          <thead className="bg-muted/50">
            <tr>
              <th colSpan="2" className="border border-border p-3 text-center font-semibold text-base">List-I</th>
              <th colSpan="2" className="border border-border p-3 text-center font-semibold text-base">List-II</th>
            </tr>
          </thead>
          <tbody className="bg-background">
            {Array.from({ length: maxRows }).map((_, idx) => {
              const listIItem = parsedData.listI[idx];
              const listIIItem = parsedData.listII[idx];
              
              return (
                <tr key={idx} className="hover:bg-muted/30 transition-colors">
                  <td className="p-3 w-12 font-bold text-center border border-border bg-muted/20">
                    {listIItem?.label}.
                  </td>
                  <td className="p-3 border border-border">
                    {listIItem?.text || ''}
                  </td>
                  <td className="p-3 w-12 font-bold text-center border border-border bg-muted/20">
                    {listIIItem?.label}.
                  </td>
                  <td 
                    className="p-3 border border-border font-mono text-sm"
                    dangerouslySetInnerHTML={{ 
                      __html: renderLatex(listIIItem?.text || '') 
                    }} 
                  />
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const QuestionManager = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Data states
  const [exams, setExams] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [loading, setLoading] = useState(false);
  
  // Filter states
  const [selectedExam, setSelectedExam] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedChapter, setSelectedChapter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterDifficulty, setFilterDifficulty] = useState('all');
  const [filterYear, setFilterYear] = useState('all');
  const [availableYears, setAvailableYears] = useState([]);
  
  // Edit state
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [expandedIds, setExpandedIds] = useState(new Set());
  
  // Bulk actions
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkActionMode, setBulkActionMode] = useState(false);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 50;

  useEffect(() => {
    if (user?.role !== 'admin') {
      toast.error('Admin access required');
      navigate('/dashboard');
      return;
    }
    loadExams();
  }, [user, navigate]);

  useEffect(() => {
    if (selectedExam) {
      loadSubjects(selectedExam);
      setSelectedSubject('');
      setSelectedChapter('');
    }
  }, [selectedExam]);

  useEffect(() => {
    if (selectedSubject) {
      loadChapters(selectedSubject);
      setSelectedChapter('');
    }
  }, [selectedSubject]);

  useEffect(() => {
    if (selectedExam) {
      loadQuestions();
      setCurrentPage(1);
    }
  }, [selectedExam, selectedSubject, selectedChapter, filterType, filterDifficulty, filterYear, searchTerm]);

  const loadExams = async () => {
    try {
      const res = await api.get('/exams');
      setExams(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('Failed to load exams:', error);
      toast.error('Failed to load exams');
    }
  };

  const loadSubjects = async (examId) => {
    try {
      const res = await api.get(`/exams/${examId}/subjects`);
      setSubjects(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('Failed to load subjects:', error);
    }
  };

  const loadChapters = async (subjectId) => {
    try {
      const res = await api.get(`/subjects/${subjectId}/chapters`);
      setChapters(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('Failed to load chapters:', error);
    }
  };

  const loadQuestions = async () => {
    if (!selectedExam || loading) return;
    
    setLoading(true);
    try {
      const params = {
        exam_id: selectedExam,
        limit: 1000000,
        offset: 0
      };
      
      if (selectedSubject) params.subject_id = selectedSubject;
      if (selectedChapter) params.chapter_id = selectedChapter;
      if (filterDifficulty !== 'all') params.difficulty = filterDifficulty;
      
      const res = await api.get('/questions', { params });
      
      let questionList = [];
      let total = 0;
      
      if (Array.isArray(res.data)) {
        questionList = res.data;
        total = res.data.length;
      } else if (res.data && Array.isArray(res.data.questions)) {
        questionList = res.data.questions;
        total = res.data.total || res.data.questions.length;
      }

      // Normalize questions
      questionList = questionList.map(q => ({
        ...q,
        options: Array.isArray(q.options) ? q.options : [],
        question_text: q.question_text || '',
        explanation: q.explanation || '',
        correct_answer: q.correct_answer || '',
        tags: Array.isArray(q.tags) ? q.tags : []
      }));

      // Extract unique years
      const years = [...new Set(questionList.map(q => q.year).filter(Boolean))].sort((a, b) => b - a);
      setAvailableYears(years);

      // Apply client-side filters
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        questionList = questionList.filter(q => 
          (q.question_text || '').toLowerCase().includes(term) ||
          (Array.isArray(q.tags) && q.tags.some(t => String(t).toLowerCase().includes(term)))
        );
      }

      if (filterYear !== 'all') {
        questionList = questionList.filter(q => String(q.year) === String(filterYear));
      }

      if (filterType === 'mcq') {
        questionList = questionList.filter(q => 
          (q.question_type || 'MCQ').toUpperCase() === 'MCQ' || 
          (Array.isArray(q.options) && q.options.length > 0)
        );
      } else if (filterType === 'numerical') {
        questionList = questionList.filter(q => 
          (q.question_type || '').toUpperCase() === 'NUMERICAL' ||
          (q.question_type || '').toUpperCase() === 'INTEGER' ||
          (!Array.isArray(q.options) || q.options.length === 0)
        );
      } else if (filterType === 'unanswered') {
        questionList = questionList.filter(q => !q.correct_answer || q.correct_answer === '');
      }
      
      setQuestions(questionList);
      setTotalQuestions(total);
    } catch (error) {
      console.error('Failed to load questions:', error);
      toast.error('Failed to load questions');
      setQuestions([]);
      setTotalQuestions(0);
    } finally {
      setLoading(false);
    }
  };

  const handleStartEdit = (question) => {
    if (!question || !question.id) return;
    
    setEditingId(question.id);
    setEditForm({
      question_text: question.question_text || '',
      options: Array.isArray(question.options) ? question.options : [],
      correct_answer: question.correct_answer || '',
      explanation: question.explanation || '',
      difficulty: question.difficulty || 'medium',
      year: question.year || '',
      question_type: question.question_type || 'MCQ',
      tags: Array.isArray(question.tags) ? question.tags : [],
      image_url: question.image_url || '',
      solution_image: question.solution_image || '',
      is_pyq: question.is_pyq || false,
      is_important: question.is_important || false
    });
  };

  const handleSaveEdit = async () => {
    if (!editingId || !editForm) return;
    
    try {
      await api.put(`/admin/questions/${editingId}`, editForm);
      toast.success('Question updated!');
      
      setQuestions(prev => 
        prev.map(q => q.id === editingId ? { ...q, ...editForm } : q)
      );
      
      setEditingId(null);
      setEditForm(null);
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to update: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditForm(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this question?')) return;
    
    try {
      await api.delete(`/admin/questions/${id}`);
      toast.success('Question deleted');
      loadQuestions();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete');
    }
  };

  const toggleSelectQuestion = (id) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === pageQuestions.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(pageQuestions.map(q => q.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) {
      toast.error('No questions selected');
      return;
    }

    if (!window.confirm(`Delete ${selectedIds.size} selected questions? This cannot be undone!`)) {
      return;
    }

    const toastId = 'bulk-delete';
    try {
      toast.loading(`Deleting ${selectedIds.size} questions...`, { id: toastId });
      
      const deletePromises = Array.from(selectedIds).map(id => 
        api.delete(`/admin/questions/${id}`)
      );
      
      await Promise.all(deletePromises);
      
      toast.success(`Successfully deleted ${selectedIds.size} questions!`, { id: toastId });
      setSelectedIds(new Set());
      setBulkActionMode(false);
      loadQuestions();
    } catch (error) {
      console.error('Bulk delete error:', error);
      toast.error('Some deletions failed', { id: toastId });
      loadQuestions(); // Refresh to show what was actually deleted
    }
  };

  const handleBulkUpdate = async (updates) => {
    if (selectedIds.size === 0) {
      toast.error('No questions selected');
      return;
    }

    const toastId = 'bulk-update';
    try {
      toast.loading(`Updating ${selectedIds.size} questions...`, { id: toastId });
      
      const updatePromises = Array.from(selectedIds).map(id => 
        api.put(`/admin/questions/${id}`, updates)
      );
      
      await Promise.all(updatePromises);
      
      toast.success(`Successfully updated ${selectedIds.size} questions!`, { id: toastId });
      setSelectedIds(new Set());
      setBulkActionMode(false);
      loadQuestions();
    } catch (error) {
      console.error('Bulk update error:', error);
      toast.error('Some updates failed', { id: toastId });
      loadQuestions();
    }
  };

  const handleImageUpload = async (file, type = 'question') => {
    try {
      toast.loading('Compressing and uploading image...', { id: 'img-upload' });
      const compressed = await compressImage(file);
      
      const fd = new FormData();
      fd.append('file', compressed);
      fd.append('exam_id', selectedExam);
      fd.append('subject_id', selectedSubject || 'general');
      fd.append('chapter_id', selectedChapter || 'general');
      fd.append('question_id', editingId || 'temp');
      fd.append('image_type', type);
      
      const res = await api.post('/admin/upload/question-image', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Image uploaded successfully!', { id: 'img-upload' });
      return res.data.url;
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Upload failed', { id: 'img-upload' });
      return null;
    }
  };

  const handlePaste = async (e, type) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) {
          const url = await handleImageUpload(file, type);
          if (url) {
            if (type === 'question') {
              setEditForm({ ...editForm, image_url: url });
            } else {
              setEditForm({ ...editForm, solution_image: url });
            }
          }
        }
        break;
      }
    }
  };

  const handleDrop = async (e, type) => {
    e.preventDefault();
    e.stopPropagation();
    
    const file = e.dataTransfer?.files?.[0];
    if (file && file.type.startsWith('image/')) {
      const url = await handleImageUpload(file, type);
      if (url) {
        if (type === 'question') {
          setEditForm({ ...editForm, image_url: url });
        } else {
          setEditForm({ ...editForm, solution_image: url });
        }
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const toggleExpand = (id) => {
    setExpandedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  // Pagination
  const totalPages = Math.ceil(questions.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const pageQuestions = questions.slice(startIndex, endIndex);

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <Card className="p-6 mb-6 bg-gradient-to-r from-purple-500/10 via-blue-500/10 to-green-500/10 border-purple-500/30">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/admin')} className="p-2">
                <ArrowLeft size={20} />
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
                  <Zap className="text-purple-500" size={28} />
                  Advanced Question Manager
                </h1>
                <p className="text-sm text-muted-foreground mt-1">
                  {totalQuestions > 0 ? `${totalQuestions} total questions` : 'Select an exam to get started'}
                  {questions.length > 0 && ` • ${questions.length} loaded`}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              {questions.length > 0 && (
                <Button 
                  onClick={() => {
                    setBulkActionMode(!bulkActionMode);
                    setSelectedIds(new Set());
                  }} 
                  variant={bulkActionMode ? "default" : "outline"}
                  className={bulkActionMode ? "bg-purple-500 hover:bg-purple-600" : ""}
                >
                  {bulkActionMode ? <Check size={16} className="mr-2" /> : <Trash2 size={16} className="mr-2" />}
                  {bulkActionMode ? 'Bulk Mode Active' : 'Bulk Delete'}
                </Button>
              )}
              <Button onClick={loadQuestions} variant="outline">
                <RefreshCw size={16} className="mr-2" /> Refresh
              </Button>
            </div>
          </div>

          {bulkActionMode && selectedIds.size > 0 && (
            <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex justify-between items-center">
              <span className="text-sm font-medium">
                {selectedIds.size} question{selectedIds.size !== 1 ? 's' : ''} selected
              </span>
              <div className="flex gap-2 flex-wrap">
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => handleBulkUpdate({ difficulty: 'easy' })}
                >
                  Set Easy
                </Button>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => handleBulkUpdate({ difficulty: 'medium' })}
                >
                  Set Medium
                </Button>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => handleBulkUpdate({ difficulty: 'hard' })}
                >
                  Set Hard
                </Button>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => handleBulkUpdate({ is_pyq: true })}
                >
                  Mark PYQ
                </Button>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => handleBulkUpdate({ is_important: true })}
                >
                  Mark Important
                </Button>
                <Button 
                  onClick={handleBulkDelete} 
                  variant="destructive"
                  size="sm"
                  className="ml-2"
                >
                  <Trash2 size={14} className="mr-1" /> Delete Selected
                </Button>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="grid grid-cols-2 md:grid-cols-7 gap-3">
            <select
              value={selectedExam}
              onChange={(e) => setSelectedExam(e.target.value)}
              className="p-2 rounded-lg border border-border bg-background text-sm"
            >
              <option value="">Select Exam</option>
              {exams.map(exam => (
                <option key={exam.id} value={exam.id}>{exam.name}</option>
              ))}
            </select>

            <select
              value={selectedSubject}
              onChange={(e) => setSelectedSubject(e.target.value)}
              disabled={!selectedExam}
              className="p-2 rounded-lg border border-border bg-background text-sm disabled:opacity-50"
            >
              <option value="">All Subjects</option>
              {subjects.map(sub => (
                <option key={sub.id} value={sub.id}>{sub.name}</option>
              ))}
            </select>

            <select
              value={selectedChapter}
              onChange={(e) => setSelectedChapter(e.target.value)}
              disabled={!selectedSubject}
              className="p-2 rounded-lg border border-border bg-background text-sm disabled:opacity-50"
            >
              <option value="">All Chapters</option>
              {chapters.map(ch => (
                <option key={ch.id} value={ch.id}>{ch.name}</option>
              ))}
            </select>

            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="p-2 rounded-lg border border-border bg-background text-sm"
            >
              <option value="all">All Types</option>
              <option value="mcq">MCQ Only</option>
              <option value="numerical">Numerical Only</option>
              <option value="unanswered">Unanswered</option>
            </select>

            <select
              value={filterDifficulty}
              onChange={(e) => setFilterDifficulty(e.target.value)}
              className="p-2 rounded-lg border border-border bg-background text-sm"
            >
              <option value="all">All Difficulty</option>
              <option value="easy">Easy</option>
              <option value="medium">Medium</option>
              <option value="hard">Hard</option>
            </select>

            <select
              value={filterYear}
              onChange={(e) => setFilterYear(e.target.value)}
              className="p-2 rounded-lg border border-border bg-background text-sm"
            >
              <option value="all">All Years</option>
              {availableYears.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>

            <Input
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="text-sm"
            />
          </div>
        </Card>

        {/* Stats Cards */}
        {selectedExam && questions.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Card className="p-4 text-center bg-purple-500/10 border-purple-500/20">
              <BarChart3 className="mx-auto mb-1 text-purple-500" size={20} />
              <p className="text-2xl font-bold text-purple-500">{totalQuestions}</p>
              <p className="text-xs text-muted-foreground">Total</p>
            </Card>
            <Card className="p-4 text-center bg-green-500/10 border-green-500/20">
              <p className="text-2xl font-bold text-green-500">
                {questions.filter(q => (q.difficulty || 'medium') === 'easy').length}
              </p>
              <p className="text-xs text-muted-foreground">Easy</p>
            </Card>
            <Card className="p-4 text-center bg-yellow-500/10 border-yellow-500/20">
              <p className="text-2xl font-bold text-yellow-500">
                {questions.filter(q => (q.difficulty || 'medium') === 'medium').length}
              </p>
              <p className="text-xs text-muted-foreground">Medium</p>
            </Card>
            <Card className="p-4 text-center bg-red-500/10 border-red-500/20">
              <p className="text-2xl font-bold text-red-500">
                {questions.filter(q => (q.difficulty || 'medium') === 'hard').length}
              </p>
              <p className="text-xs text-muted-foreground">Hard</p>
            </Card>
            <Card className="p-4 text-center bg-blue-500/10 border-blue-500/20">
              <Calendar className="mx-auto mb-1 text-blue-500" size={20} />
              <p className="text-2xl font-bold text-blue-500">
                {questions.filter(q => q.is_pyq).length}
              </p>
              <p className="text-xs text-muted-foreground">PYQs</p>
            </Card>
          </div>
        )}

        {/* Questions List */}
        {!selectedExam ? (
          <Card className="p-12 text-center border-dashed">
            <BookOpen className="mx-auto mb-4 text-muted-foreground" size={64} />
            <h3 className="text-lg font-medium text-foreground mb-2">Select an Exam</h3>
            <p className="text-muted-foreground">Choose an exam to view and manage questions</p>
          </Card>
        ) : loading ? (
          <Card className="p-12 text-center">
            <RefreshCw className="mx-auto mb-4 text-muted-foreground animate-spin" size={48} />
            <p className="text-muted-foreground">Loading questions...</p>
          </Card>
        ) : questions.length === 0 ? (
          <Card className="p-12 text-center border-dashed">
            <AlertCircle className="mx-auto mb-4 text-muted-foreground" size={48} />
            <h3 className="text-lg font-medium text-foreground mb-2">No Questions Found</h3>
            <p className="text-muted-foreground">Add questions using the scraper or import tools</p>
          </Card>
        ) : (
          <>
            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-2">
                  {bulkActionMode && (
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedIds.size === pageQuestions.length && pageQuestions.length > 0}
                        onChange={toggleSelectAll}
                        className="w-4 h-4"
                      />
                      <span className="text-sm font-medium">Select All on Page</span>
                    </label>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}

            <div className="space-y-4">
              {pageQuestions.map((question, index) => {
                const isEditing = editingId === question.id;
                const isExpanded = expandedIds.has(question.id);

                return (
                  <Card key={question.id} className="p-4">
                    {isEditing && editForm ? (
                      // EDIT MODE
                      <div className="space-y-3">
                        <div className="flex justify-between items-center mb-3">
                          <h4 className="font-bold text-amber-500">Editing Question #{startIndex + index + 1}</h4>
                          <div className="flex gap-2">
                            <Button size="sm" onClick={handleSaveEdit} className="bg-green-500 hover:bg-green-600">
                              <Save size={14} className="mr-1" /> Save
                            </Button>
                            <Button size="sm" variant="outline" onClick={handleCancelEdit}>
                              <X size={14} className="mr-1" /> Cancel
                            </Button>
                          </div>
                        </div>

                        <div>
                          <label className="text-xs font-medium mb-1 block">Question Text</label>
                          <textarea
                            value={editForm.question_text || ''}
                            onChange={(e) => setEditForm({ ...editForm, question_text: e.target.value })}
                            className="w-full p-3 rounded-lg border border-border bg-background text-sm min-h-[100px]"
                            placeholder="Enter question text"
                          />
                        </div>

                        {(editForm.question_type === 'MCQ' || editForm.question_type === 'mcq') && (
                          <div>
                            <label className="text-xs font-medium mb-1 block">Options</label>
                            <div className="grid grid-cols-2 gap-2">
                              {(editForm.options || []).map((opt, i) => (
                                <div key={i} className="flex items-center gap-2">
                                  <span className="text-sm font-bold w-6">{opt?.label || String.fromCharCode(65 + i)}</span>
                                  <input
                                    value={opt?.text || ''}
                                    onChange={(e) => {
                                      const newOpts = [...(editForm.options || [])];
                                      newOpts[i] = { label: opt?.label || String.fromCharCode(65 + i), text: e.target.value };
                                      setEditForm({ ...editForm, options: newOpts });
                                    }}
                                    className="flex-1 p-2 rounded border border-border bg-background text-sm"
                                    placeholder={`Option ${opt?.label || String.fromCharCode(65 + i)}`}
                                  />
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        <div className="grid grid-cols-4 gap-2">
                          <div>
                            <label className="text-xs mb-1 block">Answer</label>
                            <input
                              value={editForm.correct_answer || ''}
                              onChange={(e) => setEditForm({ ...editForm, correct_answer: e.target.value })}
                              className="w-full p-2 rounded border border-border bg-background text-sm"
                            />
                          </div>
                          <div>
                            <label className="text-xs mb-1 block">Difficulty</label>
                            <select
                              value={editForm.difficulty || 'medium'}
                              onChange={(e) => setEditForm({ ...editForm, difficulty: e.target.value })}
                              className="w-full p-2 rounded border border-border bg-background text-sm"
                            >
                              <option value="easy">Easy</option>
                              <option value="medium">Medium</option>
                              <option value="hard">Hard</option>
                            </select>
                          </div>
                          <div>
                            <label className="text-xs mb-1 block">Year</label>
                            <input
                              type="number"
                              value={editForm.year || ''}
                              onChange={(e) => setEditForm({ ...editForm, year: e.target.value })}
                              className="w-full p-2 rounded border border-border bg-background text-sm"
                            />
                          </div>
                          <div>
                            <label className="text-xs mb-1 block">Type</label>
                            <select
                              value={editForm.question_type || 'MCQ'}
                              onChange={(e) => setEditForm({ ...editForm, question_type: e.target.value })}
                              className="w-full p-2 rounded border border-border bg-background text-sm"
                            >
                              <option value="MCQ">MCQ</option>
                              <option value="Numerical">Numerical</option>
                            </select>
                          </div>
                        </div>

                        <div>
                          <label className="text-xs font-medium mb-1 block">Explanation</label>
                          <textarea
                            value={editForm.explanation || ''}
                            onChange={(e) => setEditForm({ ...editForm, explanation: e.target.value })}
                            className="w-full p-2 rounded border border-border bg-background text-sm min-h-[60px]"
                            placeholder="Explanation (optional)"
                          />
                        </div>

                        {/* Image Uploads */}
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-xs font-medium mb-1 block">Question Image</label>
                            <div 
                              className="flex flex-col gap-2"
                              onPaste={(e) => handlePaste(e, 'question')}
                              onDrop={(e) => handleDrop(e, 'question')}
                              onDragOver={handleDragOver}
                            >
                              <input
                                type="file"
                                accept="image/*"
                                onChange={async (e) => {
                                  if (e.target.files?.[0]) {
                                    const url = await handleImageUpload(e.target.files[0], 'question');
                                    if (url) setEditForm({ ...editForm, image_url: url });
                                  }
                                }}
                                className="hidden"
                                id="question-img-upload"
                              />
                              <label
                                htmlFor="question-img-upload"
                                className="p-3 border-2 border-dashed border-border rounded cursor-pointer hover:bg-accent hover:border-blue-400 text-center text-sm transition-colors"
                              >
                                <Upload size={18} className="inline mr-2 text-blue-500" />
                                <span className="font-medium">Click to Upload</span>
                                <div className="text-xs text-muted-foreground mt-1">
                                  or drag & drop, or Ctrl+V to paste
                                </div>
                              </label>
                              {editForm.image_url && (
                                <div className="relative">
                                  <img src={editForm.image_url} alt="Question" className="max-h-32 w-full object-contain rounded border" />
                                  <div className="flex gap-2 mt-1">
                                    <a href={editForm.image_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-500 hover:underline flex items-center gap-1">
                                      <Eye size={12} /> View Full
                                    </a>
                                    <button
                                      onClick={() => setEditForm({ ...editForm, image_url: '' })}
                                      className="text-xs text-red-500 hover:underline flex items-center gap-1"
                                    >
                                      <X size={12} /> Remove
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                          <div>
                            <label className="text-xs font-medium mb-1 block">Solution Image</label>
                            <div 
                              className="flex flex-col gap-2"
                              onPaste={(e) => handlePaste(e, 'solution')}
                              onDrop={(e) => handleDrop(e, 'solution')}
                              onDragOver={handleDragOver}
                            >
                              <input
                                type="file"
                                accept="image/*"
                                onChange={async (e) => {
                                  if (e.target.files?.[0]) {
                                    const url = await handleImageUpload(e.target.files[0], 'solution');
                                    if (url) setEditForm({ ...editForm, solution_image: url });
                                  }
                                }}
                                className="hidden"
                                id="solution-img-upload"
                              />
                              <label
                                htmlFor="solution-img-upload"
                                className="p-3 border-2 border-dashed border-border rounded cursor-pointer hover:bg-accent hover:border-green-400 text-center text-sm transition-colors"
                              >
                                <Upload size={18} className="inline mr-2 text-green-500" />
                                <span className="font-medium">Click to Upload</span>
                                <div className="text-xs text-muted-foreground mt-1">
                                  or drag & drop, or Ctrl+V to paste
                                </div>
                              </label>
                              {editForm.solution_image && (
                                <div className="relative">
                                  <img src={editForm.solution_image} alt="Solution" className="max-h-32 w-full object-contain rounded border" />
                                  <div className="flex gap-2 mt-1">
                                    <a href={editForm.solution_image} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-500 hover:underline flex items-center gap-1">
                                      <Eye size={12} /> View Full
                                    </a>
                                    <button
                                      onClick={() => setEditForm({ ...editForm, solution_image: '' })}
                                      className="text-xs text-red-500 hover:underline flex items-center gap-1"
                                    >
                                      <X size={12} /> Remove
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Toggles */}
                        <div className="flex gap-4 items-center">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={editForm.is_pyq || false}
                              onChange={(e) => setEditForm({ ...editForm, is_pyq: e.target.checked })}
                              className="w-4 h-4"
                            />
                            <span className="text-sm">Mark as PYQ (Previous Year Question)</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={editForm.is_important || false}
                              onChange={(e) => setEditForm({ ...editForm, is_important: e.target.checked })}
                              className="w-4 h-4"
                            />
                            <span className="text-sm">Mark as Important</span>
                          </label>
                        </div>
                      </div>
                    ) : (
                      // VIEW MODE
                      <div>
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex items-center gap-2">
                            {bulkActionMode && (
                              <input
                                type="checkbox"
                                checked={selectedIds.has(question.id)}
                                onChange={() => toggleSelectQuestion(question.id)}
                                className="w-4 h-4 cursor-pointer"
                              />
                            )}
                            <span className="text-sm font-bold text-muted-foreground">#{startIndex + index + 1}</span>
                            <Badge variant={question.difficulty === 'easy' ? 'default' : question.difficulty === 'medium' ? 'secondary' : 'destructive'}>
                              {question.difficulty || 'medium'}
                            </Badge>
                            <Badge variant="outline">{question.question_type || 'MCQ'}</Badge>
                            {question.is_pyq && <Badge className="bg-blue-500"><Calendar size={12} className="mr-1" /> PYQ</Badge>}
                            {question.is_important && <Badge className="bg-amber-500"><Star size={12} className="mr-1" /> Important</Badge>}
                            {question.year && <span className="text-xs text-muted-foreground">{question.year}</span>}
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" variant="ghost" onClick={() => toggleExpand(question.id)}>
                              {isExpanded ? <EyeOff size={14} /> : <Eye size={14} />}
                            </Button>
                            <Button 
                              size="sm" 
                              variant="default"
                              onClick={() => handleStartEdit(question)}
                              className="bg-blue-500 hover:bg-blue-600"
                            >
                              <Edit size={14} className="mr-1" /> Edit
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => handleDelete(question.id)} className="text-red-500 hover:text-red-600">
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </div>

                        {/* Question Text or Matching List Table */}
                        {(() => {
                          const questionText = question.question_text || '';
                          const matchingList = parseMatchingList(questionText);
                          
                          if (matchingList && isExpanded) {
                            return renderMatchingList(matchingList);
                          } else if (!matchingList) {
                            return (
                              <div 
                                className="text-sm text-foreground mb-2"
                                dangerouslySetInnerHTML={{ 
                                  __html: renderLatex(
                                    isExpanded 
                                      ? questionText
                                      : (questionText.slice(0, 200) + (questionText.length > 200 ? '...' : ''))
                                  )
                                }}
                              />
                            );
                          } else {
                            return (
                              <div className="text-sm text-foreground mb-2">
                                <span className="italic text-muted-foreground">Matching list question (expand to view)</span>
                              </div>
                            );
                          }
                        })()}

                        {!isExpanded && question.correct_answer && (
                          <div className="text-xs text-muted-foreground mt-1">
                            <strong className="text-green-500">Answer:</strong> {question.correct_answer}
                          </div>
                        )}

                        {isExpanded && Array.isArray(question.options) && question.options.length > 0 && !parseMatchingList(question.question_text || '') && (
                          <div className="grid grid-cols-2 gap-2 mt-3">
                            {question.options.map((opt) => (
                              <div 
                                key={opt?.label || 'unknown'} 
                                className={`p-2 rounded border text-sm ${
                                  opt?.label === question.correct_answer 
                                    ? 'border-green-500 bg-green-500/10' 
                                    : 'border-border'
                                }`}
                              >
                                <span className="font-bold">{opt?.label || ''}.</span> {opt?.text || ''}
                                {opt?.label === question.correct_answer && (
                                  <CheckCircle size={14} className="inline ml-2 text-green-500" />
                                )}
                              </div>
                            ))}
                          </div>
                        )}

                        {isExpanded && question.correct_answer && (
                          <div className="mt-2 p-2 bg-green-500/10 border border-green-500/30 rounded text-sm inline-block">
                            <strong className="text-green-600">Correct Answer:</strong> <span className="font-mono font-bold">{question.correct_answer}</span>
                          </div>
                        )}

                        {isExpanded && question.explanation && (
                          <div className="mt-3 p-3 bg-blue-500/10 border border-blue-500/30 rounded text-sm">
                            <strong className="text-blue-500">Explanation:</strong>{' '}
                            <span dangerouslySetInnerHTML={{ __html: renderLatex(question.explanation || '') }} />
                          </div>
                        )}

                        {isExpanded && (question.image_url || question.solution_image) && (
                          <div className="mt-3 grid grid-cols-2 gap-3">
                            {question.image_url && (
                              <div>
                                <p className="text-xs font-medium mb-1">Question Image:</p>
                                <a href={question.image_url} target="_blank" rel="noopener noreferrer">
                                  <img src={question.image_url} alt="Question" className="max-h-48 rounded border hover:opacity-80" />
                                </a>
                              </div>
                            )}
                            {question.solution_image && (
                              <div>
                                <p className="text-xs font-medium mb-1">Solution Image:</p>
                                <a href={question.solution_image} target="_blank" rel="noopener noreferrer">
                                  <img src={question.solution_image} alt="Solution" className="max-h-48 rounded border hover:opacity-80" />
                                </a>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>

            {/* Bottom Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-6">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                <span className="text-sm text-muted-foreground">
                  Page {currentPage} of {totalPages} • Showing {pageQuestions.length} of {questions.length} questions
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default QuestionManager;
